// Updated TransferFundsDialog.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Autocomplete,
  CircularProgress,
  Divider,
  Grid,
  Paper
} from '@mui/material';
import { SwapHoriz, Person, AttachMoney } from '@mui/icons-material';
import { fetchMembers } from './api';

const TransferFundsDialog = ({ 
  open, 
  onClose, 
  fromMember, 
  availableFunds, 
  onTransfer 
}) => {
  const [formData, setFormData] = useState({
    to_member_id: null,
    amount: '',
    reason: 'Fund Transfer', // Hidden but still sent to backend
    officer_name: 'Secretary' // Hidden but still sent to backend
  });
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const [membersLoading, setMembersLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inputValue, setInputValue] = useState('');

  // Debounce utility function
  const debounce = useCallback((func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }, []);

  // Load members function
  const loadMembers = useCallback(async (searchQuery) => {
    setMembersLoading(true);
    try {
      const membersList = await fetchMembers(searchQuery || '', 'all', 200);
      const filteredMembers = membersList.filter(member => member.id !== fromMember?.id);
      setMembers(filteredMembers);
    } catch (error) {
      console.error('Error loading members:', error);
      setError('Failed to load members list');
    } finally {
      setMembersLoading(false);
    }
  }, [fromMember?.id]);

  // Debounced search function
  const debouncedLoadMembers = useCallback(
    debounce(loadMembers, 500),
    [loadMembers, debounce]
  );

  // Load initial members when dialog opens
  useEffect(() => {
    if (open && fromMember) {
      loadMembers(''); // Load initial members
      // Reset form when dialog opens
      setFormData({
        to_member_id: null,
        amount: '',
        reason: 'Fund Transfer',
        officer_name: 'Secretary'
      });
      setSelectedMember(null);
      setInputValue('');
      setError('');
      setSuccess('');
    }
  }, [open, fromMember, loadMembers]);

  // Handle search input changes
  const handleInputChange = useCallback((event, newInputValue) => {
    setInputValue(newInputValue);
    if (newInputValue.length >= 2) {
      debouncedLoadMembers(newInputValue);
    }
  }, [debouncedLoadMembers]);

  // Handle member selection
  const handleMemberSelection = useCallback((event, newValue) => {
    setSelectedMember(newValue);
    setFormData(prev => ({
      ...prev,
      to_member_id: newValue ? newValue.id : null
    }));
    if (error) setError('');
  }, [error]);

  const handleFormInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.to_member_id) {
      setError('Please select a recipient member');
      return false;
    }
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid transfer amount');
      return false;
    }

    if (parseFloat(formData.amount) > availableFunds) {
      setError(`Transfer amount cannot exceed available funds (KES ${availableFunds.toLocaleString()})`);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    
    try {
      const transferData = {
        to_member_id: formData.to_member_id,
        amount: parseFloat(formData.amount),
        reason: formData.reason || 'Fund Transfer',
        officer_name: formData.officer_name
      };

      await onTransfer(transferData);
      setSuccess('Fund transfer completed successfully!');
      
      // Close dialog after 2 seconds
      setTimeout(() => {
        setSuccess('');
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Transfer error:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to transfer funds';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Don't render if success message is showing
  if (success) {
    return (
      <Dialog 
        open={open} 
        onClose={() => {}} // Prevent closing during success message
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              width: 60, 
              height: 60, 
              bgcolor: 'success.main', 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: 'white',
              fontSize: '24px'
            }}>
              ✓
            </Box>
            <Typography variant="h6" color="success.main" gutterBottom>
              Transfer Successful!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Funds have been transferred successfully to {selectedMember?.name}
            </Typography>
            <CircularProgress size={20} sx={{ mt: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Closing automatically...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        bgcolor: 'primary.main',
        color: 'white'
      }}>
        <SwapHoriz />
        Transfer Funds
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Transfer Summary */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom color="primary">
            Transfer Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Person color="action" />
                <Typography variant="subtitle2">From:</Typography>
              </Box>
              <Typography variant="body1" fontWeight="medium">
                {fromMember?.name} ({fromMember?.member_id})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Available: KES {availableFunds?.toLocaleString()}
              </Typography>
            </Grid>
            
            {selectedMember && (
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Person color="success" />
                  <Typography variant="subtitle2">To:</Typography>
                </Box>
                <Typography variant="body1" fontWeight="medium">
                  {selectedMember.name} ({selectedMember.member_id})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Group: {selectedMember.group_name}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Form Fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Recipient Selection */}
          <Autocomplete
            value={selectedMember}
            onChange={handleMemberSelection}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            options={members}
            getOptionLabel={(option) => option ? `${option.name} (${option.member_id})` : ''}
            loading={membersLoading}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            noOptionsText={inputValue.length < 2 ? "Type at least 2 characters to search" : "No members found"}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Recipient"
                placeholder="Search by name or member ID"
                required
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <Person color="action" sx={{ mr: 1 }} />,
                  endAdornment: (
                    <>
                      {membersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography variant="body1">
                    {option.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {option.member_id} • Group: {option.group_name}
                  </Typography>
                </Box>
              </li>
            )}
          />

          {/* Amount */}
          <TextField
            label="Transfer Amount (KES)"
            type="number"
            value={formData.amount}
            onChange={(e) => handleFormInputChange('amount', e.target.value)}
            required
            InputProps={{
              startAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                  <Typography variant="body2" color="action">KES</Typography>
                </Box>
              ),
            }}
            helperText={`Available: KES ${availableFunds?.toLocaleString()}`}
          />

          {/* Transfer Summary */}
          {formData.amount && selectedMember && (
            <>
              <Divider sx={{ my: 1 }} />
              <Paper sx={{ p: 2, bgcolor: 'info.lighter' }}>
                <Typography variant="subtitle1" color="info.main" gutterBottom>
                  Transfer Summary
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Transfer Amount:</Typography>
                  <Typography fontWeight="medium">
                    KES {parseFloat(formData.amount || 0).toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Remaining Balance:</Typography>
                  <Typography fontWeight="medium">
                    KES {(availableFunds - parseFloat(formData.amount || 0)).toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Recipient:</Typography>
                  <Typography fontWeight="medium">
                    {selectedMember.name}
                  </Typography>
                </Box>
              </Paper>
            </>
          )}

          {/* Error Messages */}
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.to_member_id || !formData.amount}
          startIcon={loading ? <CircularProgress size={20} /> : <SwapHoriz />}
        >
          {loading ? 'Processing...' : 'Transfer Funds'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferFundsDialog;